import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Database } from '@/integrations/supabase/types';
import { useModuleSync } from '@/hooks/useModuleSync';
import { useAuthContext } from '@/contexts/AuthContext';

export type Insurance = Database['public']['Tables']['project_insurances']['Row'];
export type InsuranceInsert = Database['public']['Tables']['project_insurances']['Insert'];
export type InsuranceUpdate = Database['public']['Tables']['project_insurances']['Update'];

export const fetchInsurancesFromDb = async (projectId?: string): Promise<Insurance[]> => {
  let query = supabase
    .from('project_insurances')
    .select('*');

  if (projectId) {
    query = query.eq('project_id', projectId);
  }

  const { data, error } = await query.order('created_at', { ascending: false });
  if (error) throw error;
  return (data as Insurance[]) || [];
};

export const useInsurance = (projectId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { emitEvent, warnings, dismissWarning } = useModuleSync();
  const { user, initialized } = useAuthContext();

  const queryKey = ['insurances', projectId ?? 'all'];

  const { data: insurances = [], isLoading: loading, refetch: fetchInsurances } = useQuery({
    queryKey,
    queryFn: () => fetchInsurancesFromDb(projectId),
    enabled: !!user && initialized,
  });

  const addInsurance = async (insurance: InsuranceInsert) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Nicht angemeldet');

      const { error } = await supabase
        .from('project_insurances')
        .insert({
          ...insurance,
          created_by: user.id,
        });

      if (error) throw error;

      toast({
        title: 'Erfolg',
        description: 'Versicherung hinzugefügt.',
      });

      queryClient.invalidateQueries({ queryKey: ['insurances'] });
      emitEvent({ type: 'create', module: 'project_insurances', data: insurance, projectId: insurance.project_id });
    } catch (error: any) {
      console.error('Error adding insurance:', error);
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Versicherung konnte nicht hinzugefügt werden.',
      });
      throw error;
    }
  };

  const updateInsurance = async (id: string, updates: InsuranceUpdate) => {
    try {
      const { error } = await supabase
        .from('project_insurances')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Erfolg',
        description: 'Versicherung aktualisiert.',
      });

      queryClient.invalidateQueries({ queryKey: ['insurances'] });
    } catch (error: any) {
      console.error('Error updating insurance:', error);
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Versicherung konnte nicht aktualisiert werden.',
      });
      throw error;
    }
  };

  const deleteInsurance = async (id: string) => {
    try {
      const { error } = await supabase
        .from('project_insurances')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Erfolg',
        description: 'Versicherung gelöscht.',
      });

      queryClient.invalidateQueries({ queryKey: ['insurances'] });
    } catch (error: any) {
      console.error('Error deleting insurance:', error);
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Versicherung konnte nicht gelöscht werden.',
      });
      throw error;
    }
  };

  return {
    insurances,
    loading,
    fetchInsurances,
    addInsurance,
    updateInsurance,
    deleteInsurance,
    moduleSyncWarnings: warnings,
    dismissModuleWarning: dismissWarning,
  };
};
